import type { DateTime } from 'luxon';
import { useMemo } from 'react';
import {
  MAX_SOURCE_EVENT_INSTANCES,
  type SourceEventInstance,
} from '@/features/sources/lib/sourceEventInstance.lib';
import type { ExperienceFeed } from '@/types/gql.generated';
import { Box, Flex, Text } from '@/ui';
import { SourceEventRow } from './SourceEventRow';

type InstanceGroup = {
  instances: SourceEventInstance[];
  monthLabel: string;
};

type Props = {
  feed: ExperienceFeed;
  /**
   * Whether the window holds any hidden occurrence, revealed or not — the one
   * thing an empty list can't tell us about itself.
   */
  hasHidden: boolean;
  /**
   * Already sorted, windowed, visibility-filtered and capped — see
   * expandSourceEventInstances.
   */
  instances: SourceEventInstance[];
  isTruncated: boolean;
  rangeEnd: DateTime;
  rangeStart: DateTime;
};

export const SourceEventList = ({
  feed,
  hasHidden,
  instances,
  isTruncated,
  rangeEnd,
  rangeStart,
}: Props) => {
  // Group by month. Hidden occurrences were already resolved by the expansion,
  // which is what keeps the row cap from being spent on rows nobody asked to
  // see. Memoized because this runs on every render and the input array (one
  // entry per occurrence) can be large.
  const groups = useMemo(() => {
    const result: InstanceGroup[] = [];
    const seen = new Map<string, InstanceGroup>();
    for (const instance of instances) {
      const key = instance.startDate.toFormat('LLLL yyyy');
      let group = seen.get(key);
      if (!group) {
        group = { instances: [], monthLabel: key };
        result.push(group);
        seen.set(key, group);
      }
      group.instances.push(instance);
    }
    return result;
  }, [instances]);

  // Nothing left to list. `hasHidden` is what separates a source with no
  // upcoming events from one whose upcoming events are all hidden, now that the
  // hidden ones never reach this component.
  if (instances.length === 0) {
    return (
      <Text color="ink.muted" fontSize="ah-control">
        {hasHidden
          ? 'All upcoming events from this source are hidden.'
          : 'No upcoming events from this source.'}
      </Text>
    );
  }

  // The list is bounded at both ends — it starts today and stops a year out —
  // and drops occurrences past a row cap. Say so, so a series that continues
  // beyond the last row doesn't read as one that ends there.
  const rangeLabel = `${rangeStart.toFormat('MMM d, yyyy')} through ${rangeEnd.toFormat('MMM d, yyyy')}`;
  const footnote = isTruncated
    ? `Showing the first ${MAX_SOURCE_EVENT_INSTANCES} events from ${rangeLabel}.`
    : `Showing events from ${rangeLabel}.`;

  return (
    <Box>
      {groups.map((group) => (
        <Box key={group.monthLabel} mb={6}>
          {/* Month heading — serif per mockup .srcd-monthlabel */}
          <Text
            color="ink.strong"
            fontFamily="Georgia, serif"
            fontSize="22px"
            mb={2}
          >
            {group.monthLabel}
          </Text>
          <Box bg="ink.strong" h="2px" mb={3} opacity={0.85} />

          <Flex direction="column" gap={2}>
            {group.instances.map((instance) => (
              <SourceEventRow
                feed={feed}
                instance={instance}
                key={instance.key}
              />
            ))}
          </Flex>
        </Box>
      ))}

      <Text color="ink.muted" fontSize="ah-status">
        {footnote}
      </Text>
    </Box>
  );
};
